import ENDPOINTS from "@/app/constants/api/api"
import utf8ToBase64 from "@/app/utils/utf8ToBase64"
import { apiGet, apiPatch, apiPost } from "@/lib/api"
import type {
  AuthHistoryData,
  AuthHistoryParams,
  ChangePasswordPayload,
  ChangeTwoFactorPayload,
  GoogleAuthenticatorData,
  LoginRecoveryCompletePayload,
  LoginRecoveryOtpPayload,
  LoginRecoveryTokens,
  LoginRecoveryVerifyPayload,
  SwitchTwoFactorPayload,
  VerifyCurrentPasswordPayload,
  VerifyCurrentPasswordResponse,
  TwoFactorCodePayload,
  TwoFactorOtpAction,
  TwoFactorOtpResponse,
} from "@/app/components/Dashboard/Security/security.interface"

type ApiResponse<T> = { message: T }

export const changePassword = (payload: ChangePasswordPayload) =>
  apiPost<unknown>(ENDPOINTS.SECURITY_PASSWORD_CHANGE, payload)

export const verifyCurrentPassword = (payload: VerifyCurrentPasswordPayload) =>
  apiPost<VerifyCurrentPasswordResponse>(ENDPOINTS.SECURITY_PASSWORD_VERIFY_CURRENT, payload)

export const activateGoogleAuthenticator = () =>
  apiPost<{ message?: GoogleAuthenticatorData }>(
    ENDPOINTS.SECURITY_GOOGLE_AUTHENTICATOR_ACTIVE,
    {},
  )

export const verifyGoogleAuthenticator = (code: string) =>
  apiPost<unknown>(ENDPOINTS.SECURITY_GOOGLE_AUTHENTICATOR_VERIFY, { code })

export const changeTwoFactor = (payload: ChangeTwoFactorPayload) =>
  apiPatch<unknown>(ENDPOINTS.SECURITY_TWO_FACTOR, payload)

export const getAuthHistory = (params: AuthHistoryParams) =>
  apiGet<{ message?: AuthHistoryData }>(ENDPOINTS.SECURITY_AUTH_HISTORY, params)

// 2FA v3 (PRD pageId=120815990)

export const sendTwoFactorOtp = async (action: TwoFactorOtpAction): Promise<TwoFactorOtpResponse> =>
  (await apiPost<ApiResponse<TwoFactorOtpResponse>>(ENDPOINTS.SECURITY_2FA_SEND_OTP, { action })).message

export const startAuthenticatorActivation = async (payload: TwoFactorCodePayload) =>
  (await apiPost<ApiResponse<GoogleAuthenticatorData>>(ENDPOINTS.SECURITY_GOOGLE_AUTHENTICATOR_ACTIVE, payload))
    .message

export const confirmAuthenticatorActivation = (code: string) =>
  apiPost<unknown>(ENDPOINTS.SECURITY_GOOGLE_AUTHENTICATOR_VERIFY, { code })

export const switchTwoFactorMethod = (payload: SwitchTwoFactorPayload) =>
  apiPatch<unknown>(ENDPOINTS.SECURITY_TWO_FACTOR, payload)

export const deactivateAuthenticator = (code: string) =>
  apiPost<unknown>(ENDPOINTS.SECURITY_GOOGLE_AUTHENTICATOR_DEACTIVATE, { code })

// PRD 8: forgot during login. SMS OTP first, then password; the last call logs the user in.
export const sendLoginRecoveryOtp = async (payload: LoginRecoveryOtpPayload): Promise<TwoFactorOtpResponse> =>
  (await apiPost<ApiResponse<TwoFactorOtpResponse>>(ENDPOINTS.SECURITY_2FA_LOGIN_RECOVERY_SEND_OTP, payload)).message

export const verifyLoginRecoveryOtp = async (payload: LoginRecoveryVerifyPayload): Promise<TwoFactorOtpResponse> =>
  (await apiPost<ApiResponse<TwoFactorOtpResponse>>(ENDPOINTS.SECURITY_2FA_LOGIN_RECOVERY_VERIFY_OTP, payload))
    .message

export const completeLoginRecovery = async (payload: LoginRecoveryCompletePayload): Promise<LoginRecoveryTokens> =>
  (
    await apiPost<ApiResponse<LoginRecoveryTokens>>(ENDPOINTS.SECURITY_2FA_LOGIN_RECOVERY_COMPLETE, {
      ...payload,
      password: utf8ToBase64(payload.password),
    })
  ).message

// PRD 9: forgot from the withdrawal page. No code; the backend revokes every session, the caller logs out.
export const recoverAuthenticatorFromWithdrawal = () =>
  apiPost<unknown>(ENDPOINTS.SECURITY_2FA_WITHDRAWAL_RECOVERY, {})
